import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { Action, select, Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import {
  IntroductionEditorComponent,
  MediaEditorComponent,
} from "src/app/editors/components";
import { deleteActivity } from "src/app/levels/components/level/store/actions";
import { AppState, BuildActivity, BuildActivityStep } from "src/app/models";
import { ValidationListComponent } from "src/app/validation-builder";
import { ActivityEditorBaseComponent } from "../activity";
import { ActivityTogglesComponent } from "../activity-toggles";
import { EditInMakecodeComponent } from "../edit-in-makecode";
import { BuildActivityStepComponent } from "./build-activity-step.component";
import { addStep, deleteStep, update } from "./store/actions";
import {
  selectBuildActivity,
  selectIsLoading,
  selectSteps,
} from "./store/selectors";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { ActivityStepperComponent } from "../activity-stepper";

@Component({
  selector: "app-build-activity",
  templateUrl: "./build-activity.component.html",
  styleUrls: ["./build-activity.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuildActivityComponent
  extends ActivityEditorBaseComponent
  implements OnInit
{
  constructor(
    store$: Store<AppState>,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(store$);
    this.activityType = "Build";
  }

  @HostBinding("class") class = "app-build-activity";

  @ViewChild(IntroductionEditorComponent)
  introduction: IntroductionEditorComponent;

  @ViewChild(ActivityTogglesComponent)
  activityToggles: ActivityTogglesComponent;

  @ViewChild(EditInMakecodeComponent)
  codeEditor: EditInMakecodeComponent;

  @ViewChild(ValidationListComponent)
  validationItems: ValidationListComponent;

  @ViewChildren(BuildActivityStepComponent)
  steps = new QueryList<BuildActivityStepComponent>();

  @ViewChild(MediaEditorComponent)
  mediaEditor: MediaEditorComponent;

  @ViewChild(ActivityStepperComponent)
  activityStepper: ActivityStepperComponent;

  get valid(): boolean {
    return !!(
      this.title?.valid &&
      this.introduction?.valid &&
      this.activityToggles?.valid &&
      this.codeEditor?.valid &&
      this.validationItems?.valid &&
      this.steps?.map((s) => s).every((s) => s.valid)
    );
  }

  get dirty(): boolean {
    return !!(
      this.title?.dirty ||
      this.introduction?.dirty ||
      this.activityToggles?.dirty ||
      this.codeEditor?.dirty ||
      this.validationItems?.dirty ||
      this.mediaEditor?.dirty ||
      this.steps?.map((s) => s).find((s) => s.dirty)
    );
  }

  set activity(activity: BuildActivity) {
    this.myActivity = activity;
    this.cdr.detectChanges();
  }

  get activity(): BuildActivity {
    return this.myActivity;
  }

  steps$: Observable<BuildActivityStep[]> = this.store$.pipe(
    select(selectSteps),
    tap((steps) => (this.mySteps = steps))
  );

  loading$: Observable<boolean> = this.store$.select(selectIsLoading);

  private myActivity: BuildActivity;
  private mySteps: BuildActivityStep[] = [];

  ngOnInit(): void {
    this.store$
      .pipe(select(selectBuildActivity), takeUntil(this.destroyed$))
      .subscribe((activity) => (this.activity = activity));
  }

  addStep(): void {
    this.store$.dispatch(addStep({ activityId: this.activity.id }));
  }

  deleteStep(index: number): void {
    this.store$.dispatch(deleteStep({ step: this.mySteps[index] }));
  }

  drop(event: CdkDragDrop<string[]>) {
    // const items = [...this.mySteps.map((step) => step)]; // Convert to array new array
    const items = [...this.steps.map((s) => s.value)];

    const newArr = this.array_move(
      items,
      event.previousIndex,
      event.currentIndex
    );

    this.mySteps = newArr;

    this.activityStepper.changeSelectedIndex(event.currentIndex);

    setTimeout(() => {
      this.steps?.forEach((s) => {
        s.instructions.instructionsControl.markAsDirty();
        console.log(s.instructions, s.dirty);
      });
      this.cdr.detectChanges();
    }, 500);
  }

  array_move(arr, old_index, new_index) {
    const oldItemIndex = arr[old_index];
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    if (old_index <= new_index) {
      const newArr = arr.map((item, index) => {
        const newItem = { ...item };
        if (
          Math.min(old_index, new_index) < index &&
          Math.max(old_index, new_index) >= index
        ) {
          newItem.id = arr[index - 1].id;
        } else if (Math.min(old_index, new_index) === index) {
          newItem.id = oldItemIndex.id;
        }
        return newItem;
      });
      return newArr;
    } else {
      const newArr = arr.map((item, index) => {
        const newItem = { ...item };
        if (
          Math.min(old_index, new_index) <= index &&
          Math.max(old_index, new_index) > index
        ) {
          newItem.id = arr[index + 1].id;
        } else if (Math.max(old_index, new_index) === index) {
          newItem.id = oldItemIndex.id;
        }
        return newItem;
      });
      return newArr;
    }
  }

  protected getUpdateAction(): Action {
    return update({
      activity: {
        ...this.activity,
        title: this.title.value,
        introduction: this.introduction.value,
        hasLowThreshold: this.activityToggles.hasLowThreshold,
        lowThreshold: this.activityToggles.lowThreshold,
        hasPassingThreshold: this.activityToggles.hasPassingThreshold,
        passingThreshold: this.activityToggles.passingThreshold,
        peerReview: this.activityToggles.peerReview,
        numOfReviews: this.activityToggles.numOfReviews,
        project: this.codeEditor?.project,
        validationItems: this.validationItems.items,
      },
      steps: this.steps.filter((s) => s.dirty).map((s) => s.value),
      mediaFile: this.mediaEditor.mediaValue,
    });
  }

  protected getDeleteAction(): Action {
    return deleteActivity({ activity: this.activity });
  }
}
